import { documentObject, windowObject } from '@fold-dev/core'
import chrono from 'chrono-node'
import { useEffect } from 'react'
import { getNextValidDate } from '../../helpers/dates'

export const weekdays = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY']

export type RichInputEventName = 'onenter'

export const dispatchRichInputEvent = (eventName: RichInputEventName, data: any = {}) => {
    documentObject.dispatchEvent(new CustomEvent(eventName, { detail: data }))
}

export const useRichInputEvent = (event: RichInputEventName, handler, passive = false) => {
    useEffect(() => {
        documentObject.addEventListener(event, handler, passive)
        return () => documentObject.removeEventListener(event, handler)
    })
}

export function findMarkdownFormats(str) {
    const patterns = [
        { regex: /\*\*(.+?)\*\*/g, tag: 'strong', offset: 2 },
        { regex: /\*(.+?)\*/g, tag: 'em', offset: 1 },
        { regex: /~~(.+?)~~/g, tag: 'del', offset: 2 },
        // Add hyperlink pattern
        { regex: /\[(.+?)\]\((.+?)\)/g, tag: 'a', offset: 2, isLink: true },
        // Add arrow conversion pattern
        { regex: /->/g, tag: 'arrow', isArrow: true },
    ]

    const matches = []

    for (const { regex, tag, offset, isLink, isArrow } of patterns) {
        let match
        regex.lastIndex = 0 // Reset regex state
        while ((match = regex.exec(str)) !== null) {
            if (isArrow) {
                // For arrow conversion, mark the entire match for replacement
                matches.push({
                    matchIndex: match.index,
                    start: match.index,
                    end: match.index + 2, // Length of "->"
                    isArrow: true,
                    fullMatch: '->',
                    totalLength: 2,
                })
            } else if (isLink) {
                // For hyperlinks, handle the special case with text and URL
                const linkText = match[1]
                const url = match[2]
                const start = match.index + 1 // Skip the opening [
                const end = start + linkText.length
                // Calculate the total length of the markdown construct
                const totalLength = match[0].length
                matches.push({
                    start,
                    end,
                    tag,
                    offset,
                    matchIndex: match.index,
                    isLink: true,
                    url,
                    fullMatch: match[0],
                    totalLength,
                })
            } else {
                const start = match.index + offset
                const end = start + match[1].length
                matches.push({ start, end, tag, offset, matchIndex: match.index })
            }
        }
    }

    matches.sort((a, b) => a.matchIndex - b.matchIndex)

    return matches
}

export const createDateNode = (version) => {
    const entity = documentObject.createElement('mark')

    entity.className = 'f-rich-input-entity'
    entity.contentEditable = 'false'
    entity.tabIndex = 1
    entity.dataset.type = 'date'
    entity.dataset.version = version

    return entity
}

export const createEntityNode = ({ id, color, name, image, type, value }) => {
    const entity = documentObject.createElement('a')

    entity.className = 'f-rich-input-entity'
    entity.contentEditable = 'false'
    entity.tabIndex = 1

    entity.id = id
    entity.innerHTML = name
    entity.dataset.image = image
    entity.dataset.value = value
    entity.dataset.type = type
    entity.dataset.color = color
    entity.style.color = color

    return entity
}

export const getWordBoundary = (text: string, position: number) => {
    let start
    let end

    for (let i = position; i >= 0; i--) {
        if (text[i] == ' ') {
            start = i
            break
        }
    }

    switch (start) {
        case undefined:
            start = 0
            break
        case 0:
            start = 0
            break
        default:
            start = start + 1
    }

    for (let i = position; i <= text.length; i++) {
        if (text[i] == ' ') {
            end = i
            break
        }
    }

    switch (end) {
        case undefined:
            end = text.length
            break
        default:
            end = end
    }

    return { start, end }
}

export const getModifiedWordBoundary = (text: string, position: number) => {
    let boundary = getWordBoundary(text, position)
    if (position == boundary.end) boundary = getWordBoundary(text, position - 1)
    return boundary
}

export const getWindowSelection = (): Selection => {
    return windowObject.getSelection() || new Selection()
}

export const unwrapNode = (node) => {
    if (!node || !node.parentNode) {
        console.error('Invalid node or node has no parent')
        return
    }

    while (node.firstChild) {
        node.parentNode.insertBefore(node.firstChild, node)
    }

    node.remove()
}

export const getPreviousNode = (node) => {
    if (node.previousSibling) {
        let lastChild = node.previousSibling
        while (lastChild.lastChild) {
            lastChild = lastChild.lastChild
        }
        return lastChild
    }

    return node.parentNode
}

export const getNodeAtCursor = () => {
    const selection = window.getSelection()
    if (!selection.rangeCount) return null

    const range = selection.getRangeAt(0)
    const startContainer = range.startContainer
    const startOffset = range.startOffset

    if (startContainer.nodeType === Node.TEXT_NODE) {
        if (startOffset > 0) {
            const precedingRange = document.createRange()
            precedingRange.setStart(startContainer, 0)
            precedingRange.setEnd(startContainer, startOffset)
            return {
                node: startContainer,
                text: precedingRange.toString(),
            }
        }

        return getPreviousNode(startContainer)
    }

    if (startOffset > 0) {
        return startContainer.childNodes[startOffset - 1]
    }

    return getPreviousNode(startContainer)
}

export const getWordAtCaret = () => {
    const selection: Selection = getWindowSelection()
    const range: Range = selection.getRangeAt(0)
    const { startContainer, startOffset } = range
    const { textContent } = startContainer
    const text = textContent || ''
    const { start, end } = getModifiedWordBoundary(text, startOffset)
    const word = text.substring(start, end)

    // Get the last letter
    // If the last letter is a space then ignore
    // contentEditable will assign the spacve to the previous word
    // if there is no "next" word in the sentence
    return word.charAt(word.length - 1) == ' ' ? null : word
}

export const unwrapDataSetDate = (parentElement) => {
    const existingDiv = parentElement.querySelector('[data-type="date"]')

    if (existingDiv) {
        while (existingDiv.firstChild) {
            existingDiv.parentNode.insertBefore(existingDiv.firstChild, existingDiv)
        }
        existingDiv.remove()
    }
}

export const selectAll = (parentElement) => {
    const range = document.createRange()
    const selection = window.getSelection()
    range.selectNodeContents(parentElement)
    selection.removeAllRanges()
    selection.addRange(range)
}

export const moveCursorToEnd = (parentElement) => {
    const range = document.createRange()
    const selection = window.getSelection()
    if (parentElement) range.selectNodeContents(parentElement)
    range.collapse(false)
    selection.removeAllRanges()
    selection.addRange(range)
}

export const insertEntity = ({ id, color, name, image, type, value }, rangeCache, callback) => {
    const selection: Selection = getWindowSelection()

    // Manually add it our saved range
    // from the cached version
    selection.removeAllRanges()
    selection.addRange(rangeCache)

    const range: Range = selection.getRangeAt(0)
    const { startContainer, startOffset } = range
    const { textContent } = startContainer
    const text = textContent || ''
    const { start, end } = getModifiedWordBoundary(text, startOffset)

    // Create a mention object
    const node = createEntityNode({ id, color, name, image, type, value })
    const textNode = documentObject.createTextNode('\u00A0')

    // Set the range to word at the caret
    range.setStart(startContainer, start)
    range.setEnd(startContainer, end)
    range.deleteContents()

    // will be blank if there is another entity node in front
    if (!documentObject.getSelection().anchorNode.nodeValue.trim()) {
        range.insertNode(textNode)
        range.setStartAfter(textNode)
    }

    // insert entity node
    range.insertNode(node)

    // Clear the selection
    const r: Range = range.cloneRange()
    r.setStartAfter(node)
    r.collapse(true)
    selection.removeAllRanges()
    selection.addRange(r)

    // Close the menu
    callback()
}

function getWeekdaysArray(day) {
    const weekdays = {
        sunday: [0],
        monday: [1],
        tuesday: [2],
        wednesday: [3],
        thursday: [4],
        friday: [5],
        saturday: [6],
        weekday: [1, 2, 3, 4, 5],
        weekend: [6],
    }

    return weekdays[day]
}

// Helper functions
function addDays(date, days) {
    const newDate = new Date(date)
    newDate.setDate(newDate.getDate() + days)
    return newDate
}

function addMonths(date, months) {
    const newDate = new Date(date)
    newDate.setMonth(newDate.getMonth() + months)
    return newDate
}

function getFirstOfNextMonth() {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth() + 1, 1)
}

function getNextMonday() {
    const now = new Date()
    const day = now.getDay()
    const daysUntilMonday = day === 0 ? 1 : 8 - day
    now.setDate(now.getDate() + daysUntilMonday)
    return now
}

// ----------------------------------------------------------------------------

export function parseNLPDate(input, ignoreList = []) {
    const now = new Date()

    // Patterns for common date phrases including weekdays
    const datePatterns = [
        {
            pattern:
                /every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekday)\s+at\s+([0-9]{1,2})(:[0-9]{2})?\s?(am|pm)?/i,
            handler: (match, index) => handleRecurringWeekdayWithTime(match, index),
        },
        {
            pattern: /every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekday|weekend)/i,
            handler: (match, index) => handleRecurringWeekday(match, index),
        },
        {
            pattern: /every\s+(day|weekday)\s+at\s+([0-9]{1,2})(:[0-9]{2})?\s?(am|pm)?/i,
            handler: (match, index) => handleRecurringWithTime(match, index),
        },
        {
            pattern: /every\s+(day|week|month|weekday)/i,
            handler: (match, index) => handleRecurringSimple(match, index),
        },
        {
            pattern:
                /\b(\d{1,2})(st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+at\s+([0-9]{1,2})(:[0-9]{2})?\s?(am|pm)?/i,
            handler: (match, index) => handleSpecificDateWithTime(match, index),
        },
        {
            pattern:
                /\b(\d{1,2})(st|nd|rd|th)?\s+(January|February|March|April|May|June|July|August|September|October|November|December)\b/i,
            handler: (match, index) => handleSpecificDateSimple(match, index),
        },
        { pattern: /next\s+week/i, handler: (match, index) => handleSimpleDate(getNextMonday(), match, index) },
        { pattern: /next\s+month/i, handler: (match, index) => handleSimpleDate(getFirstOfNextMonth(), match, index) },
    ]

    // Try to match input with any known date pattern
    for (const { pattern, handler } of datePatterns) {
        const match = input?.match(pattern)

        if (match && ignoreList.includes(match[0])) {
            const index = input.indexOf(match[0])
            return handler(match, index)
        }
    }

    return null // No match found
}

// Handle recurring patterns with weekday and time (e.g., "every Tuesday at 2pm")
function handleRecurringWeekdayWithTime(match, index) {
    const weekday = match[1] // monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekday
    const hour = parseInt(match[2], 10)
    const minute = match[3] ? parseInt(match[3].substring(1), 10) : 0
    const ampm = match[4] ? match[4].toLowerCase() : null

    let finalHour = hour
    if (ampm === 'pm' && hour < 12) {
        finalHour += 12
    }
    if (ampm === 'am' && hour === 12) {
        finalHour = 0
    }

    const endIndex = index + match[0].length

    return {
        frequency: 'weekday',
        weekday: getWeekdaysArray(weekday.toLowerCase()),
        time: { hour: finalHour, minute },
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

// Handle recurring simple patterns with weekday (e.g., "every Tuesday")
function handleRecurringWeekday(match, index) {
    const weekday = match[1] // monday|tuesday|wednesday|thursday|friday|saturday|sunday|weekday|weekend
    const endIndex = index + match[0].length

    return {
        frequency: 'weekday',
        weekday: getWeekdaysArray(weekday.toLowerCase()),
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

// Handle recurring patterns with time (e.g., "every day at 2pm")
function handleRecurringWithTime(match, index) {
    const period = match[1] // day|weekday
    const hour = parseInt(match[2], 10)
    const minute = match[3] ? parseInt(match[3].substring(1), 10) : 0
    const ampm = match[4] ? match[4].toLowerCase() : null

    let finalHour = hour
    if (ampm === 'pm' && hour < 12) {
        finalHour += 12
    }
    if (ampm === 'am' && hour === 12) {
        finalHour = 0
    }

    const endIndex = index + match[0].length

    return {
        frequency: period,
        weekday: period == 'weekday' ? [0, 1, 2, 3, 4, 5, 6] : undefined,
        time: { hour: finalHour, minute },
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

// Handle recurring simple patterns (e.g., "every day")
function handleRecurringSimple(match, index) {
    const period = match[1] // day|week|month|weekday
    const endIndex = index + match[0].length

    return {
        frequency: period,
        weekday: period == 'weekday' ? [0, 1, 2, 3, 4, 5, 6] : undefined,
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

// Handle specific date with time (e.g., "15th September at 9:45am")
function handleSpecificDateWithTime(match, index) {
    const day = parseInt(match[1], 10)
    const month = match[3] // January|February|March|April|May|June|July|August|September|October|November|December
    const year = new Date().getFullYear()
    const hour = parseInt(match[4], 10)
    const minute = match[5] ? parseInt(match[5].substring(1), 10) : 0
    const ampm = match[6] ? match[6].toLowerCase() : null

    let finalHour = hour
    if (ampm === 'pm' && hour < 12) {
        finalHour += 12
    }
    if (ampm === 'am' && hour === 12) {
        finalHour = 0
    }

    const dateString = `${month} ${day}, ${year} ${finalHour}:${minute}`
    const date = new Date(dateString)
    const endIndex = index + match[0].length

    return {
        date,
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

// Handle simple specific date (e.g., "15th September")
function handleSpecificDateSimple(match, index) {
    const day = parseInt(match[1], 10)
    const month = match[3] // January|February|March|April|May|June|July|August|September|October|November|December
    const year = new Date().getFullYear()

    const dateString = `${month} ${day}, ${year}`
    const date = new Date(dateString)
    const endIndex = index + match[0].length

    return {
        date,
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

// Handle simple date phrases (e.g., "tomorrow", "today", etc.)
function handleSimpleDate(date, match, index) {
    const endIndex = index + match[0].length

    return {
        date,
        match: match[0],
        startIndex: index,
        endIndex,
    }
}

export const createDateFromString = (version, text) => {
    if (version == 'default') {
        const parsedDate: any = parseNLPDate(text)
        const { frequency, weekday = [], time, date } = parsedDate

        const repeat = frequency
            ? {
                  interval: 1, // every frequency
                  frequency, // weekday|day|week|month
                  weekday, // [0...6]
                  from: new Date(), // today
                  to: null, // infinite
                  repitions: null, // infinite
              }
            : null

        const start = frequency ? getNextValidDate(repeat) : date

        if (time) {
            start.setHours(time.hour)
            start.setMinute(time.minute)
        }

        return {
            start,
            end: null,
            wholeDay: !!time,
            repeat,
        }
    } else {
        const isNoon = (date: Date) => {
            const hour = date.getHours()
            const minute = date.getMinutes()
            const second = date.getSeconds()
            return hour === 12 && minute === 0 && second === 0
        }

        const parsedDate = chrono.parse(text)[0]

        return {
            start: parsedDate.start.date(),
            end: parsedDate.end?.date(),
            wholeDay: isNoon(parsedDate.start.date()),
            repeat: null,
        }
    }
}

export const processHTML = async (html) => {
    const encapsulated = `<div>${html}</div>`.replace(/&nbsp;/g, ' ')
    const fragment = new DOMParser().parseFromString(encapsulated, 'text/xml')
    const entities = fragment.getElementsByClassName('f-rich-input-entity')

    const labels = []
    const users = []
    let dates = null

    Array.from(entities).forEach((entity) => {
        const id = entity.getAttribute('id')
        const value = entity.getAttribute('data-value')
        const type = entity.getAttribute('data-type')
        const color = entity.getAttribute('data-color')
        const image = entity.getAttribute('data-image')
        const name = entity.getAttribute('data-name')
        const version = entity.getAttribute('data-version')
        const text = entity.textContent

        entity.remove()

        switch (type) {
            case 'user':
                users.push({ id, image, name: value })
                break
            case 'label':
                labels.push({ id, text: value, color })
                break
            case 'date':
                dates = createDateFromString(version, text)
                break
        }
    })

    const title = fragment.documentElement.innerHTML.replace(/  +/g, ' ')

    return {
        labels,
        users,
        title,
        dates,
    }
}
